import { Image, Paperclip } from "lucide-react";
import { KEYS } from "platejs";
import { useEditorRef } from "platejs/react";
import { memo, useCallback, useRef } from "react";
import { cn } from "../../tailwind-utils";

export const ImageToolbarButton = memo(function ImageToolbarButton() {
  const editor = useEditorRef();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const url = reader.result as string;
        editor.tf.insertNodes(
          { type: KEYS.img, url, children: [{ text: "" }] },
          { removeEmpty: true },
        );
        editor.tf.focus();
      };
      reader.readAsDataURL(file);

      // Reset input so the same file can be re-selected
      e.target.value = "";
    },
    [editor],
  );

  return (
    <>
      <button
        className={cn(
          "inline-flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md px-1.5",
          "hover:bg-muted hover:text-muted-foreground",
          "[&_svg:not([class*='size-'])]:size-4",
        )}
        onClick={() => inputRef.current?.click()}
        type="button"
      >
        <Image />
      </button>
      <input
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        ref={inputRef}
        type="file"
      />
    </>
  );
});

interface FileToolbarButtonProps {
  onFileSelect?: (files: File[]) => void;
}

export const FileToolbarButton = memo(function FileToolbarButton({
  onFileSelect,
}: FileToolbarButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      onFileSelect?.([...files]);
      e.target.value = "";
    },
    [onFileSelect],
  );

  return (
    <>
      <button
        className={cn(
          "inline-flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md px-1.5",
          "hover:bg-muted hover:text-muted-foreground",
          "[&_svg:not([class*='size-'])]:size-4",
        )}
        onClick={() => inputRef.current?.click()}
        type="button"
      >
        <Paperclip />
      </button>
      <input
        className="hidden"
        multiple
        onChange={handleFileChange}
        ref={inputRef}
        type="file"
      />
    </>
  );
});
